/**
 * Category Preview Tree Component
 *
 * Displays a hierarchical preview of categories before import.
 * Shows stats, level indicators, and action buttons.
 *
 * Date: 2025-11-20
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Loader2, Check, X, FolderTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import type { TaxonomyNode } from './GoogleTaxonomySearch';

interface CategoryPreviewTreeProps {
  rootNode: TaxonomyNode;
  sectorId?: number;
  onImport: () => void;
  onCancel: () => void;
  loading?: boolean;
}

interface SubtreeStats {
  totalNodes: number;
  rootNode: TaxonomyNode;
  maxLevel: number;
  levelCounts: { [key: string]: number };
}

export function CategoryPreviewTree({
  rootNode,
  sectorId,
  onImport,
  onCancel,
  loading = false,
}: CategoryPreviewTreeProps) {
  const [stats, setStats] = useState<SubtreeStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLevels, setExpandedLevels] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    loadSubtreeStats();
  }, [rootNode.id]);

  const loadSubtreeStats = async () => {
    setIsLoadingStats(true);
    setError(null);

    try {
      const response = await authFetch(
        `${API_URL}/google-taxonomy/${rootNode.id}/tree?stats=true`
      );

      if (!response.ok) {
        throw new Error('Failed to load category tree');
      }

      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      } else {
        setError(data.error || 'Failed to load tree');
      }
    } catch (err) {
      console.error('Load tree error:', err);
      setError('Failed to load category tree. Please try again.');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const toggleLevel = (level: number) => {
    const newExpanded = new Set(expandedLevels);
    if (newExpanded.has(level)) {
      newExpanded.delete(level);
    } else {
      newExpanded.add(level);
    }
    setExpandedLevels(newExpanded);
  };

  if (isLoadingStats) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-600">Loading category tree...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="text-center text-red-600 py-4">{error}</div>
          <div className="flex justify-center">
            <Button onClick={loadSubtreeStats} variant="outline">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              Category Import Preview
            </CardTitle>
            <CardDescription className="mt-2">
              {stats.totalNodes} categories will be imported under "{stats.rootNode.name}"
            </CardDescription>
          </div>
          <Badge variant="secondary" className="text-lg px-3 py-1">
            {stats.totalNodes} Total
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Stats Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{stats.totalNodes}</div>
              <div className="text-xs text-gray-600">Total Categories</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{stats.maxLevel + 1}</div>
              <div className="text-xs text-gray-600">Levels Deep</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {Object.keys(stats.levelCounts).length}
              </div>
              <div className="text-xs text-gray-600">Distinct Levels</div>
            </div>
          </div>
        </div>

        {/* Level Breakdown */}
        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-700">Categories by Level</div>
          <div className="space-y-1">
            {Object.entries(stats.levelCounts)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([level, count]) => (
                <button
                  key={level}
                  onClick={() => toggleLevel(parseInt(level))}
                  className="w-full flex items-center justify-between p-2 rounded hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {expandedLevels.has(parseInt(level)) ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-sm">
                      Level {level}
                      {parseInt(level) === 0 && ' (Root)'}
                    </span>
                  </div>
                  <Badge variant="outline">{count} categories</Badge>
                </button>
              ))}
          </div>
        </div>

        {/* Import Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-blue-800">
            <div className="font-semibold mb-1">What happens during import:</div>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>All {stats.totalNodes} categories will be created</li>
              <li>Parent-child relationships will be preserved</li>
              <li>You can mark "primary levels" for research after import</li>
              <li>Existing categories will be skipped (no duplicates)</li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={onImport}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Import {stats.totalNodes} Categories
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
